/**
 * Third-Party Scripts Lazy Loader
 *
 * @description
 * Loads deferred third-party scripts on user interaction to improve initial page load performance.
 * Scripts are loaded on first user activity (mouse, keyboard, touch, scroll) or immediately
 * if they were previously loaded (tracked via sessionStorage).
 *
 * @example
 * Scripts with data-lazy-src attribute will be loaded on user interaction.
 */

(() => {
  const userActivityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'keypress', 'touchmove'];
  const SCRIPTS_LOADED_FLAG = 'third-party-scripts-loaded';
  const LOADING_CLASS = 'third-party-scripts-loading';

  const isDeferralEnabled = () => document.documentElement.dataset.thirdPartyDeferralEnabled !== 'false';

  /**
   * @param {string} url
   * @returns {string}
   */
  const normalizeScriptUrl = (url) => url.split('#')[0].split('?')[0];

  async function activateDeferredScripts() {
    document.documentElement.classList.add(LOADING_CLASS);

    /** @type {HTMLScriptElement[]} */
    const scriptTagsForLoad = Array.from(document.querySelectorAll('script[data-lazy-src]'));
    /** @type {HTMLLinkElement[]} */
    const preloadLinksForLoad = Array.from(document.querySelectorAll('link[data-lazy-href]'));

    const scriptsLoad = [];
    const scriptUrlsToExecute = new Set();

    for (const scriptTag of scriptTagsForLoad) {
      const lazySrc = scriptTag.dataset.lazySrc;

      if (!lazySrc) {
        continue;
      }

      const normalizedUrl = normalizeScriptUrl(lazySrc);

      if (scriptUrlsToExecute.has(normalizedUrl)) {
        scriptTag.removeAttribute('data-lazy-src');
        continue;
      }

      scriptUrlsToExecute.add(normalizedUrl);
      scriptTag.src = lazySrc;

      const scriptLoad = new Promise((resolve) => {
        scriptTag.onload = resolve;
        scriptTag.onerror = resolve;
      });

      scriptsLoad.push(scriptLoad);
    }

    for (const preloadLink of preloadLinksForLoad) {
      const lazyHref = preloadLink.dataset.lazyHref;

      if (!lazyHref) {
        continue;
      }

      const normalizedHref = normalizeScriptUrl(lazyHref);

      if (scriptUrlsToExecute.has(normalizedHref)) {
        preloadLink.removeAttribute('data-lazy-href');
        continue;
      }

      preloadLink.href = lazyHref;
    }

    await Promise.all(scriptsLoad);

    document.documentElement.classList.remove(LOADING_CLASS);

    window.dispatchEvent(new CustomEvent('load-head-scripts'));

    sessionStorage.setItem(SCRIPTS_LOADED_FLAG, 'true');

    window.dispatchEvent(new CustomEvent('third-party-scripts-loaded'));

    document.documentElement.classList.add('third-party-scripts-loaded');
  }

  async function onUserActivity() {
    for (const event of userActivityEvents) {
      window.removeEventListener(event, onUserActivity);
    }

    await activateDeferredScripts();
  }

  function startListenUserActivity() {
    for (const event of userActivityEvents) {
      window.addEventListener(event, onUserActivity);
    }
  }

  window.addEventListener('load', () => {
    if (!isDeferralEnabled()) {
      activateDeferredScripts();
      return;
    }

    if (sessionStorage.getItem(SCRIPTS_LOADED_FLAG)) {
      onUserActivity();
    } else {
      startListenUserActivity();
    }
  });
})();
